/**
 * Workflows feature tab.
 *
 * Data anchor: workflow runner-minute billing in `credit_transactions`,
 * surfaced via the `/workflow/usage/*` endpoints. AI calls from workflow
 * steps are shown in workspace:ai under "AI in Workflows", not here.
 */
import { useI18n } from '@affine/i18n';
import { useCallback } from 'react';

import { fetchWorkflowsByUser, type WorkflowUserUsageItem } from '../api';
import { formatCount, formatCredits } from '../format';
import type { SummaryCardData } from '../tab-summary-card';
import { TabSummaryCards } from '../tab-summary-card';
import type { AnalyticsPeriodRange } from '../types';
import { useAsyncResource } from '../use-async-resource';
import { WorkflowExecutionsTable } from './workflow-executions-table';

export interface WorkflowsTabProps {
  workspaceId: string;
  period: string | AnalyticsPeriodRange;
}

export const WorkflowsTab = ({ workspaceId, period }: WorkflowsTabProps) => {
  const t = useI18n();

  const fetcher = useCallback(
    () => fetchWorkflowsByUser(workspaceId, period),
    [workspaceId, period]
  );

  const { data: users } = useAsyncResource<WorkflowUserUsageItem[]>(
    `workflows-by-user:${workspaceId}:${period}`,
    fetcher
  );

  // Compute the total from the by-user aggregation so the card matches the
  // runner-minute ledger bucket exposed by the workflow usage API.
  const totalCredits = users?.reduce((sum, u) => sum + u.credits, 0) ?? 0;
  const totalExecutions = users?.reduce((sum, u) => sum + u.executions, 0) ?? 0;

  const summary: readonly SummaryCardData[] = [
    {
      label:
        t[
          'com.streamlain.settings.workspace.ai.analytics.card.total-credits'
        ]?.() ?? 'Total credits',
      value: formatCredits(totalCredits),
    },
    {
      label:
        t[
          'com.streamlain.settings.workspace.ai.analytics.card.executions'
        ]?.() ?? 'Executions',
      value: formatCount(totalExecutions),
    },
  ];

  return (
    <>
      <TabSummaryCards cards={summary} />
      <WorkflowExecutionsTable workspaceId={workspaceId} period={period} />
    </>
  );
};
